import { useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import {
  LocalPharmacy as PharmaIcon,
  Storefront as RetailIcon,
  Inventory2 as InventoryIcon,
  Restaurant as FoodIcon,
  Devices as ElectronicsIcon,
  Checkroom as ApparelIcon,
  Build as HardwareIcon,
  DirectionsCar as AutoIcon,
  FormatQuote as QuoteIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { GlassHeader } from '@/components/Layout/GlassHeader';
import { brand } from '@/theme/theme';

interface Service {
  icon: React.ReactNode;
  title: string;
  body: string;
  tint: string;
}

const SERVICES: Service[] = [
  {
    icon: <PharmaIcon />,
    title: 'Pharmaceuticals',
    body:
      'Batch-wise inventory, expiry tracking, GST-compliant invoicing and DL-number records for distributors and chemists.',
    tint: '#A78BFA',
  },
  {
    icon: <RetailIcon />,
    title: 'Wholesale & Retail',
    body:
      'Multi-tier pricing, bulk order workflows, rep-led ordering and outstanding tracking across your retailer network.',
    tint: '#7B61FF',
  },
  {
    icon: <FoodIcon />,
    title: 'Food & Beverage',
    body:
      'Recipe-aware stock, perishable rotation, supplier-side procurement and HSN-tagged invoicing for FSSAI compliance.',
    tint: '#F59E0B',
  },
  {
    icon: <ElectronicsIcon />,
    title: 'Electronics',
    body:
      'Serial-tracked inventory, warranty registration, IMEI capture and after-sales service tickets in one screen.',
    tint: '#06B6D4',
  },
  {
    icon: <ApparelIcon />,
    title: 'Apparel & Lifestyle',
    body:
      'Size / colour matrix, season planning, channel-wise margin views and store-level reorder forecasting.',
    tint: '#EC4899',
  },
  {
    icon: <HardwareIcon />,
    title: 'Hardware & Industrial',
    body:
      'SKU variants, B2B quotation flows, dispatch challans and aged-stock reports for industrial supply chains.',
    tint: '#10B981',
  },
  {
    icon: <AutoIcon />,
    title: 'Automotive Parts',
    body:
      'Vehicle-fitment lookup, multi-brand catalogues, fast-moving SKU heatmaps and warranty replacement flows.',
    tint: '#3B82F6',
  },
  {
    icon: <InventoryIcon />,
    title: 'Stationery & FMCG',
    body:
      'High-velocity SKU management, scheme-driven pricing, route plans for sales reps and same-day reconciliation.',
    tint: '#EF4444',
  },
];

interface Testimonial {
  quote: string;
  name: string;
  role: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'We migrated our entire wholesale pharma billing in a weekend. Batch-wise expiry tracking alone saved us a full inventory audit.',
    name: 'Ramesh Kulkarni',
    role: 'Director, Mahalaxmi Pharma Distributors',
  },
  {
    quote:
      'The rep-led ordering flow doubled our daily order volume. Our field team finally has a tool that respects how they actually work.',
    name: 'Anita Reddy',
    role: 'Sales Head, Sri Sai Foods',
  },
  {
    quote:
      'Outstanding-tracking across 140 retailers used to be a nightmare. Interface gives us the ageing report on the dashboard every morning.',
    name: 'Vikas Sharma',
    role: 'Owner, Sharma Hardware Mart',
  },
];

export function LandingPage() {
  const navigate = useNavigate();

  // Direct links like ardinotech.com/#about land here before React has
  // rendered the #about element, so the browser's native hash-scroll fires
  // too early and silently does nothing. Retry the scroll after mount.
  useEffect(() => {
    if (!window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <GlassHeader
        center={
          <Stack direction="row" spacing={3.5} alignItems="center">
            {[
              { label: 'Features', href: '#features' },
              { label: 'Services', href: '#services' },
              { label: 'About', href: '#about' },
              { label: 'Terms & Conditions', to: '/terms' },
              { label: 'Privacy Policy', to: '/privacy' },
            ].map((item) => (
              <Typography
                key={item.label}
                component={item.to ? RouterLink : 'a'}
                {...(item.to ? { to: item.to } : { href: item.href })}
                sx={{
                  fontWeight: 500,
                  fontSize: 14.5,
                  whiteSpace: 'nowrap',
                  color: brand.navy,
                  textDecoration: 'none',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -6,
                    left: 0,
                    width: 0,
                    height: 2,
                    background: brand.violet,
                    transition: 'width 0.25s ease',
                  },
                  '&:hover::after': { width: '100%' },
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Stack>
        }
      />

      {/* ----- Hero / Features ----- */}
      <Container id="features" maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="caption"
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                mb: 2.5,
                borderRadius: 99,
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: brand.violet,
                background: 'rgba(123, 97, 255, 0.1)',
                border: `1px solid rgba(123, 97, 255, 0.2)`,
              }}
            >
              Smart Business Management Platform
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.4rem', md: '3.6rem' },
                lineHeight: 1.05,
                color: brand.navy,
                mb: 2.5,
              }}
            >
              Manage. Scale.{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${brand.violet} 0%, ${brand.violetSoft} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Automate.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: brand.inkSoft,
                maxWidth: 560,
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              One platform for inventory, billing, retailer ordering and field
              sales. Built for Indian wholesalers, retailers and brand
              representatives across pharma, FMCG, electronics, hardware and
              more.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/register-tenant')}
                sx={{ minWidth: 180, height: 52, fontSize: 16 }}
              >
                Register Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  minWidth: 180,
                  height: 52,
                  fontSize: 16,
                  color: brand.navy,
                  borderColor: 'rgba(11, 21, 48, 0.2)',
                  '&:hover': {
                    borderColor: brand.navy,
                    background: 'rgba(11, 21, 48, 0.03)',
                  },
                }}
              >
                Sign in
              </Button>
            </Stack>
          </Grid>

          {/* Decorative glass card on the right */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4/5',
                maxHeight: 480,
              }}
            >
              {/* Floating tile 1 */}
              <Card
                sx={{
                  position: 'absolute',
                  top: '8%',
                  left: '4%',
                  width: '70%',
                  p: 2.5,
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(12px)',
                  border: `1px solid ${brand.glassBorder}`,
                  transform: 'rotate(-3deg)',
                }}
              >
                <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                  Today's revenue
                </Typography>
                <Typography variant="h2" sx={{ color: brand.navy, mt: 0.5 }}>
                  ₹4,82,310
                </Typography>
                <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>
                  ↑ 18.4% vs yesterday
                </Typography>
              </Card>

              {/* Floating tile 2 */}
              <Card
                sx={{
                  position: 'absolute',
                  bottom: '12%',
                  right: '0%',
                  width: '65%',
                  p: 2.5,
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(12px)',
                  border: `1px solid ${brand.glassBorder}`,
                  transform: 'rotate(2deg)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(123, 97, 255, 0.12)',
                      color: brand.violet,
                    }}
                  >
                    <InventoryIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                      Open orders
                    </Typography>
                    <Typography variant="h3" sx={{ color: brand.navy }}>
                      127
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    height: 4,
                    background: 'rgba(11, 21, 48, 0.06)',
                    borderRadius: 99,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: '68%',
                      height: '100%',
                      background: `linear-gradient(90deg, ${brand.violet}, ${brand.violetSoft})`,
                    }}
                  />
                </Box>
              </Card>

              {/* Glow blob */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: -1,
                  background: `radial-gradient(circle at 50% 50%, ${brand.violetGlow}, transparent 60%)`,
                  filter: 'blur(40px)',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ----- Services ----- */}
      <Box id="services" sx={{ background: 'rgba(255,255,255,0.5)', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: brand.violet, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              What we do
            </Typography>
            <Typography variant="h2" sx={{ color: brand.navy }}>
              Built for the way Indian businesses actually run
            </Typography>
            <Typography sx={{ maxWidth: 640, color: brand.inkSoft }}>
              Workflows tuned for pharma, FMCG, electronics and seven other
              verticals — without forcing you into a generic ERP shape.
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {SERVICES.map((s) => (
              <Grid item xs={12} sm={6} md={3} key={s.title}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px -16px rgba(11, 21, 48, 0.15)`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      background: `${s.tint}1F`,
                      color: s.tint,
                      mb: 2,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1, color: brand.navy }}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.inkSoft, lineHeight: 1.6 }}>
                    {s.body}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ----- Testimonials / About ----- */}
      <Container id="about" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: brand.violet, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Customer stories
          </Typography>
          <Typography variant="h2" sx={{ color: brand.navy }}>
            Trusted by businesses across India
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {TESTIMONIALS.map((t) => (
            <Grid item xs={12} md={4} key={t.name}>
              <Card sx={{ height: '100%', p: 3.5, position: 'relative', overflow: 'visible' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -16,
                    left: 24,
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(135deg, ${brand.violet}, ${brand.violetSoft})`,
                    color: 'white',
                    boxShadow: `0 8px 20px -6px ${brand.violetGlow}`,
                  }}
                >
                  <QuoteIcon fontSize="small" />
                </Box>
                <Typography sx={{ color: brand.ink, lineHeight: 1.7, mt: 2.5, mb: 3 }}>
                  {t.quote}
                </Typography>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: brand.navy }}>{t.name}</Typography>
                  <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                    {t.role}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ----- Footer ----- */}
      <Box sx={{ borderTop: `1px solid ${brand.glassBorder}`, py: 4 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
          >
            <Typography variant="body2" sx={{ color: brand.inkSoft }}>
              © {new Date().getFullYear()} Ardinotech Private Limited
            </Typography>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <MuiLink component={RouterLink} to="/terms" sx={{ fontSize: 14, color: brand.inkSoft }}>
                Terms &amp; Conditions
              </MuiLink>
              <MuiLink component={RouterLink} to="/privacy" sx={{ fontSize: 14, color: brand.inkSoft }}>
                Privacy Policy
              </MuiLink>
            </Stack>
            <Typography variant="body2" sx={{ color: brand.inkSoft }}>
              Built in India · GST · DL · FSSAI ready
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
